import { useIntl } from 'react-intl';

import { Input, Select } from 'antd';

import { CheckboxSegmented } from '@src/components/CheckboxSegmented';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import IntlMessages from '@src/util/IntlMessages';

import {
  DEFAULT_DAYS_OPTIONS,
  DEFAULT_SHIFT_TYPE_OPTIONS,
  SHIFT_STATE_FILTER_OPTIONS,
  SHIFT_STATUS,
} from '../../constants';
import {
  buildShiftFilterSetValueProps,
  getShiftStatusFilterLabel,
  SHIFT_FILTER_DEFAULTS,
  SHIFT_LIST_FILTER_FIELDS,
} from '../../shiftsListFilterConfig';
import type { SelectOption } from '../../types';

const { Search } = Input;
const { Option } = Select;

interface ShiftFiltersProps {
  filters: FilterValues;
  handleFilters: (props: SetValueProps) => void;
  loading?: boolean;
  shiftTypeOptions?: SelectOption[];
  daysOptions?: SelectOption[];
}

/**
 * Filters for the shifts table (SecondaryPanel content).
 */
const ShiftFilters = ({
  filters,
  handleFilters,
  loading = false,
  shiftTypeOptions = DEFAULT_SHIFT_TYPE_OPTIONS,
  daysOptions = DEFAULT_DAYS_OPTIONS,
}: ShiftFiltersProps) => {
  const intl = useIntl();

  const rawSelectedDays = filters[SHIFT_LIST_FILTER_FIELDS.SELECTED_DAYS]?.value;
  const selectedDays = Array.isArray(rawSelectedDays)
    ? rawSelectedDays.map(day => {
        const asNumber = Number(day);
        return Number.isNaN(asNumber) ? day : asNumber;
      })
    : [];

  const shiftTypeValue = String(
    filters[SHIFT_LIST_FILTER_FIELDS.SHIFT_TYPE]?.value ?? SHIFT_FILTER_DEFAULTS.SHIFT_TYPE,
  );

  const statusValue = String(
    filters[SHIFT_LIST_FILTER_FIELDS.STATUS]?.value ?? SHIFT_STATUS.ACTIVE,
  );
  const exceptionalValue = filters[SHIFT_LIST_FILTER_FIELDS.EXCEPTIONAL]?.value;
  const exceptionalSelectValue =
    typeof exceptionalValue === 'boolean' ? String(exceptionalValue) : undefined;

  return (
    <div className="gx-flex-column gx-guarnic-gap-2">
      <div>
        <label className="gx-mb-1 gx-d-block" htmlFor={SHIFT_LIST_FILTER_FIELDS.SEARCH_TERM}>
          <IntlMessages id="shifts.filter.name" />
        </label>
        <Search
          allowClear
          className="gx-m-0"
          placeholder={intl.formatMessage({ id: 'shifts.filter.searchPlaceholder' })}
          value={String(filters[SHIFT_LIST_FILTER_FIELDS.SEARCH_TERM]?.value ?? '')}
          onChange={e =>
            handleFilters(
              buildShiftFilterSetValueProps(
                SHIFT_LIST_FILTER_FIELDS.SEARCH_TERM,
                e.target.value,
                intl,
              ),
            )
          }
          disabled={loading}
          id={SHIFT_LIST_FILTER_FIELDS.SEARCH_TERM}
        />
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block" htmlFor={SHIFT_LIST_FILTER_FIELDS.SHIFT_TYPE}>
          <IntlMessages id="shifts.filter.type" />
        </label>
        <Select
          allowClear
          className="gx-w-100"
          value={shiftTypeValue || undefined}
          onChange={value => {
            const selectedOption = shiftTypeOptions.find(option => option.value === value);
            handleFilters(
              buildShiftFilterSetValueProps(
                SHIFT_LIST_FILTER_FIELDS.SHIFT_TYPE,
                value ?? SHIFT_FILTER_DEFAULTS.SHIFT_TYPE,
                intl,
                selectedOption?.label ?? intl.formatMessage({ id: 'shifts.type.all' }),
              ),
            );
          }}
          disabled={loading}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          id={SHIFT_LIST_FILTER_FIELDS.SHIFT_TYPE}
        >
          <Option value={SHIFT_FILTER_DEFAULTS.SHIFT_TYPE}>
            <IntlMessages id="shifts.type.all" />
          </Option>
          {shiftTypeOptions.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block" htmlFor={SHIFT_LIST_FILTER_FIELDS.EXCEPTIONAL}>
          <IntlMessages id="shifts.table.exceptional" />
        </label>
        <Select
          allowClear
          className="gx-w-100"
          value={exceptionalSelectValue}
          onChange={value => {
            const exceptional =
              value === 'true' ? true : value === 'false' ? false : SHIFT_FILTER_DEFAULTS.EXCEPTIONAL;
            handleFilters(
              buildShiftFilterSetValueProps(SHIFT_LIST_FILTER_FIELDS.EXCEPTIONAL, exceptional, intl),
            );
          }}
          disabled={loading}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          id={SHIFT_LIST_FILTER_FIELDS.EXCEPTIONAL}
        >
          <Option value="true">
            <IntlMessages id="button.yes" />
          </Option>
          <Option value="false">
            <IntlMessages id="button.no" />
          </Option>
        </Select>
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block">
          <IntlMessages id="shifts.filter.days" />
        </label>
        <CheckboxSegmented
          fluid
          options={daysOptions}
          value={selectedDays}
          onChange={value =>
            handleFilters(
              buildShiftFilterSetValueProps(SHIFT_LIST_FILTER_FIELDS.SELECTED_DAYS, value, intl),
            )
          }
          disabled={loading}
        />
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block" htmlFor={SHIFT_LIST_FILTER_FIELDS.STATUS}>
          <IntlMessages id="shifts.filter.status" />
        </label>
        <Select
          className="gx-w-100"
          allowClear
          value={statusValue === SHIFT_FILTER_DEFAULTS.STATUS ? undefined : statusValue}
          onChange={value =>
            handleFilters(
              buildShiftFilterSetValueProps(
                SHIFT_LIST_FILTER_FIELDS.STATUS,
                value ?? SHIFT_FILTER_DEFAULTS.STATUS,
                intl,
              ),
            )
          }
          disabled={loading}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          id={SHIFT_LIST_FILTER_FIELDS.STATUS}
        >
          {SHIFT_STATE_FILTER_OPTIONS.filter(option => option !== SHIFT_FILTER_DEFAULTS.STATUS).map(
            option => (
              <Option key={option} value={option}>
                {getShiftStatusFilterLabel(option, intl)}
              </Option>
            ),
          )}
        </Select>
      </div>
    </div>
  );
};

export default ShiftFilters;
